import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Box, Typography, CircularProgress } from "@mui/material";
import Input from "../../common/InputField";
import { fetchMailDomainStatus } from "../../../store/features/mailDomainAuth";
import {
  fetchBusinessTicketFromEmail,
  patchBusinessTicketFromEmail,
  clearBusinessTicketFromEmailError,
} from "../../../store/features/businessTicketFromEmail";
import "./style.css";

const bodyTextSx = {
  color: "#405261",
  fontFamily: "Inter",
  fontSize: "14px",
  fontWeight: 400,
  lineHeight: "22px",
};

const headingTextSx = {
  color: "#061522",
  fontFamily: "Inter",
  fontSize: "14px",
  fontWeight: 500,
  lineHeight: "22px",
  mb: 1.5,
};

function getErrorMessage(payload) {
  if (!payload) return "Something went wrong";
  if (typeof payload === "string") return payload;
  return (
    payload.message ||
    payload.error ||
    (Array.isArray(payload.errors) && payload.errors[0]?.message) ||
    "Something went wrong"
  );
}

function BusinessTicketConfirmationEmail() {
  const dispatch = useDispatch();
  const roleName = useSelector(
    (state) => state.loginToken?.response?.user?.role_name,
  );
  const { statusData, statusLoading: domainStatusLoading } = useSelector(
    (state) => state.mailDomainAuth,
  );
  const {
    ticket_from_email: storedEmail,
    fetchLoading,
    saveLoading,
    error: requestError,
    fetchAttempted,
  } = useSelector((state) => state.businessTicketFromEmail);

  const [emailInput, setEmailInput] = useState("");

  const domainVerified = statusData?.mail_domain_verified === true;
  const domainStatusReady = statusData != null;
  const isAllowed = roleName === "admin_with_billing";

  useEffect(() => {
    dispatch(fetchMailDomainStatus());
  }, [dispatch]);

  // GET {ROOT_URL}/business/:businessId/ticket-from-email — pre-fills business confirmation email
  useEffect(() => {
    if (!isAllowed) return;
    dispatch(fetchBusinessTicketFromEmail());
  }, [dispatch, isAllowed]);

  // Pre-fill (or clear) from GET /business/:businessId/ticket-from-email once loaded
  useEffect(() => {
    if (!fetchAttempted) return;
    setEmailInput(storedEmail ?? "");
  }, [fetchAttempted, storedEmail]);

  useEffect(() => {
    if (!requestError) return;
    toast.error(getErrorMessage(requestError));
    dispatch(clearBusinessTicketFromEmailError());
  }, [requestError, dispatch]);

  const handleSave = async () => {
    const v = emailInput.trim();
    // if (!v) {
    //   toast.error("Enter an email address");
    //   return;
    // }
    const action = await dispatch(
      patchBusinessTicketFromEmail({ ticket_from_email: v }),
    );
    if (patchBusinessTicketFromEmail.fulfilled.match(action)) {
      setEmailInput(v);
      toast.success("Business ticket confirmation email saved.");
    }
    // Rejected: slice sets `error`; single toast via requestError effect
  };

  if (!isAllowed) {
    return (
      <div className="profile-main-container">
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Typography sx={{ ...bodyTextSx, color: "#991B1B" }}>
            Only an admin with billing access can manage the business ticket
            confirmation email.
          </Typography>
        </Box>
      </div>
    );
  }

  return (
    <div className="profile-main-container">
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          p: { xs: 2, md: 3 },
          gap: { xs: 4, md: 2 },
        }}
      >
        <Box
          sx={{
            maxWidth: { xs: "100%", md: 300 },
          }}
          className="Personal-Info-Text-Container"
        >
          <Typography
            sx={{
              color: "#061522",
              fontFamily: "Inter",
              fontSize: { xs: "16px", md: "18px" },
              fontWeight: 500,
              lineHeight: "22px",
              mb: 1,
            }}
          >
            Business Ticket Confirmation
          </Typography>
          <Typography
            sx={{
              color: "#405261",
              fontFamily: "Inter",
              fontSize: { xs: "13px", md: "14px" },
              fontWeight: 400,
              lineHeight: "22px",
            }}
          >
            Set the default address used for ticket emails for your organization
            when a team member has not set their own sender under{" "}
            <Box component="span" sx={{ fontWeight: 600, color: "#061522" }}>
              My ticket email
            </Box>
            . Requires a verified domain under{" "}
            <Box component="span" sx={{ fontWeight: 600, color: "#061522" }}>
              Custom domain
            </Box>
            .
          </Typography>
        </Box>

        <Box
          sx={{
            width: { xs: "100%", md: "552px" },
            pr: { md: 7 },
          }}
        >
          {domainStatusReady && !domainVerified && !domainStatusLoading && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: "8px",
                bgcolor: "#FFFBEB",
                border: "1px solid #FCD34D",
              }}
            >
              <Typography
                sx={{
                  ...bodyTextSx,
                  fontSize: 13,
                  color: "#92400E",
                }}
              >
                Verify your domain on the{" "}
                <Box component="span" sx={{ fontWeight: 600 }}>
                  Custom domain
                </Box>{" "}
                tab before saving this email.
              </Typography>
            </Box>
          )}

          <Typography sx={headingTextSx}>
            Default business sender (tickets)
          </Typography>
          <Typography sx={{ ...bodyTextSx, mb: 2 }}>
            This address applies org-wide as a fallback. Individual users can
            override it from My ticket email after the domain is verified.
          </Typography>

          <div className="Spacing-Bottom">
            <Input
              title="Business confirmation email"
              placeholder="e.g. tickets@yourdomain.com"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              disable={
                !domainVerified ||
                fetchLoading ||
                saveLoading ||
                domainStatusLoading
              }
            />
          </div>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              width: "100%",
              mt: 2.5,
            }}
          >
            <button
              style={{ marginTop: 0, float: "none" }}
              className={`Personal-Information-Save-Button${
                saveLoading
                  ? " Personal-Information-Save-Button--primary-loading"
                  : ""
              }`}
              type="button"
              onClick={handleSave}
              disabled={
                !domainVerified ||
                saveLoading ||
                fetchLoading ||
                domainStatusLoading
              }
            >
              {saveLoading ? (
                <CircularProgress size={22} sx={{ color: "#fff" }} />
              ) : (
                "Save Changes"
              )}
            </button>
          </Box>
        </Box>
      </Box>
    </div>
  );
}

export default BusinessTicketConfirmationEmail;
